import { Modal, Text } from '@kula-ai/components';
import {
  AUTOMATION_STOP_CANCEL_CTA,
  AUTOMATION_STOP_CONFIRM_CTA,
  getAutomationStopBody,
  getAutomationStopTitle,
  type AutomationStopActivity,
  type AutomationStopScope,
} from './automationStopCopy';

// Every one of these confirms opens from inside a drawer (activity editor,
// candidate drawer, ScheduleInterview). kula's Modal defaults sit below the
// drawer band, so pin the overlay + content above it — otherwise the backdrop
// dims nothing and the dialog renders behind the drawer it belongs to.
const OVERLAY_Z_INDEX = 1450;
const CONTENT_Z_INDEX = 1500;

export type AutomationStopConfirmDialogProps = {
  isOpen: boolean;
  // The activity being edited — drives the title and body nouns.
  activity: AutomationStopActivity;
  // Stage-level surface vs a single candidate's application.
  scope: AutomationStopScope;
  // Title override for a surface that edits one PART of the activity —
  // ScheduleInterview's scorecard edit reads "Save interview scorecard?".
  title?: string;
  // Proceeds with the save; the caller stops the automation.
  onConfirm: () => void;
  // Dismisses the dialog, leaving the edit and the automation untouched.
  onCancel: () => void;
  isLoading?: boolean;
};

// The single confirm shown before saving an edit that stops a stage automation.
// Replaces the three modal components (kula Modal / ModalWithForm /
// ModalWrapper) these surfaces used to render, which is why the same warning
// looked different depending on where you hit it.
export const AutomationStopConfirmDialog = ({
  isOpen,
  activity,
  scope,
  title,
  onConfirm,
  onCancel,
  isLoading = false,
}: AutomationStopConfirmDialogProps) => {
  if (!isOpen) return null;

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && !isLoading) onCancel();
      }}
      title={title ?? getAutomationStopTitle(activity)}
      overlayZIndex={OVERLAY_Z_INDEX}
      contentZIndex={CONTENT_Z_INDEX}
      primaryButton={{
        label: AUTOMATION_STOP_CONFIRM_CTA,
        onClick: onConfirm,
        isLoading,
      }}
      secondaryButton={{
        label: AUTOMATION_STOP_CANCEL_CTA,
        onClick: onCancel,
        disabled: isLoading,
      }}
    >
      <Text variant="p">{getAutomationStopBody(activity, scope)}</Text>
    </Modal>
  );
};
